/*
 * wsfnl-funnel.js — WideStep BareFlex Pro funnel interactive engine
 *
 * Clean vanilla-JS reimplementation of the removed Webflow "os" engine.
 * Drives BOTH pages from one file; each initializer self-detects whether its
 * target DOM exists and no-ops otherwise. LOCAL prototype only — no backend,
 * no fetch, no synthetic .click(). Idempotent: safe to run once on
 * DOMContentLoaded (guards against double-init).
 *
 * Wired to the DOM attribute contract in GROUND-TRUTH.md:
 *   WS2 (offer page)  — tier cards, order-summary totals, reservation clock.
 *   WS3 (cart page)   — add-a-color slots, per-slot color/size selects (image
 *                        swap), order-summary totals, reservation clock.
 *   WS1 (advertorial) — scroll progress bar.
 *
 * Offer economics (mirror wsfnl-product-data.js; fall back if that file is
 * absent so this engine is self-sufficient):
 *   perPair    1→$69.95  2→$63/ea  3→$56/ea      anchor $125
 *   savingsPct 1→44%     2→50%     3→55%  (authoritative whole-order % vs anchor)
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------- *
   * 0. Shared config + tiny helpers
   * ---------------------------------------------------------------------- */

  var DATA = window.WSFNL_PRODUCT || {};

  var ANCHOR = DATA.anchor || 125;
  var PER_PAIR = DATA.perPair || { 1: 69, 2: 63, 3: 56 };
  var SAVINGS = DATA.savingsPct || { 1: 44, 2: 50, 3: 55 };
  var RESERVE_SECONDS = 600; // "10:00" reservation clock per GROUND-TRUTH.

  // Per-color hero PNGs (fall back to the locked CDN URLs from product-data).
  var IMAGES = DATA.images || {
    Black: 'https://cdn.shopify.com/s/files/1/0662/4061/0473/files/Black_dcabc298-8ac4-4e9a-b274-ae444b2a2a91.png?v=1766778868',
    White: 'https://cdn.shopify.com/s/files/1/0662/4061/0473/files/White_9675384c-292f-42d2-b299-e600c9dba5bd.png?v=1766778868',
    Gray:  'https://cdn.shopify.com/s/files/1/0662/4061/0473/files/Grey_b017f20e-78ca-408a-bf9c-de26d536b4eb.png?v=1766778868',
    Beige: 'https://cdn.shopify.com/s/files/1/0662/4061/0473/files/Beige_6f2c495f-12dc-47fc-b16f-0a9896738a32.png?v=1766778868',
    Pink:  'https://cdn.shopify.com/s/files/1/0662/4061/0473/files/Pink.png?v=1766778868'
  };

  // Full BareFlex Pro PDP carousel image set (hero + marketing cards + every colour),
  // used to populate the WS2 product gallery. Full-resolution Shopify CDN URLs.
  var _CDN = 'https://cdn.shopify.com/s/files/1/0662/4061/0473/files/';
  var GALLERY = DATA.gallery || [
    _CDN + 'Black_dcabc298-8ac4-4e9a-b274-ae444b2a2a91.png?v=1766778868',
    _CDN + '1_fbdfccb5-6be4-403f-bf01-7abdc0137288.png?v=1783495382',
    _CDN + '6_f982137c-8fc6-467d-82e1-0d6e49321f65.png?v=1783495413',
    _CDN + '7_28121253-23bd-48e6-90d0-ee8dbbac8cae.png?v=1783495418',
    _CDN + 'White_9675384c-292f-42d2-b299-e600c9dba5bd.png?v=1766778868',
    _CDN + 'Grey_b017f20e-78ca-408a-bf9c-de26d536b4eb.png?v=1766778868',
    _CDN + 'Beige_6f2c495f-12dc-47fc-b16f-0a9896738a32.png?v=1766778868',
    _CDN + 'Pink.png?v=1766778868'
  ];

  // Money formatter: 69.95 → "$69.95", 63 → "$63", 125 → "$125".
  // Whole dollars render with no decimals to match the captured markup
  // ("$63/each", "$125"); fractional values keep 2 decimals ("$69.95").
  function money(n) {
    if (typeof DATA.money === 'function' && n % 1 !== 0) return DATA.money(n);
    return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2));
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function qsa(root, sel) {
    return (root || document).querySelectorAll(sel);
  }

  // Normalize a raw color <option> value/text to a canonical IMAGES key.
  // The captured selects still carry an old placeholder palette; map every
  // legacy value onto a real WideStep color so the image swap always resolves.
  function resolveColorKey(raw) {
    if (!raw) return null;
    var s = String(raw).trim().toLowerCase();
    var map = {
      black: 'Black',
      white: 'White',
      gray: 'Gray', grey: 'Gray', 'dark-gray': 'Gray', 'dark gray': 'Gray',
      beige: 'Beige',
      pink: 'Pink',
      // Legacy placeholder colors with no WideStep equivalent → nearest neutral.
      orange: 'Beige',
      blue: 'Gray'
    };
    if (map[s]) return map[s];
    // Fallback: first IMAGES key whose lowercase name is a substring.
    for (var k in IMAGES) {
      if (Object.prototype.hasOwnProperty.call(IMAGES, k) &&
          s.indexOf(k.toLowerCase()) !== -1) return k;
    }
    return null;
  }

  /* ---------------------------------------------------------------------- *
   * 1. OFFER PAGE (WS2) — tier selector + order summary
   *
   *   Tier cards:   [os-element="card"]; the tier number lives in either
   *                 os-option or pb-option (capture split them). Selected card
   *                 carries .os--selected. Clicking a card selects it (add
   *                 .os--selected to the clicked card, strip it from siblings)
   *                 and recomputes the order totals.
   *
   *   Totals:       [data-os-price="sale"]           = perPair(tier) × tier
   *                 [data-os-price="regular"]         = ANCHOR × tier  ("$125")
   *                 [data-os-price="saving-percent"]  = "N% OFF"
   * ---------------------------------------------------------------------- */

  function tierOf(card) {
    var v = card.getAttribute('os-option') || card.getAttribute('pb-option');
    var n = parseInt(v, 10);
    return (n >= 1 && n <= 3) ? n : null;
  }

  function initOffer() {
    var cards = qsa(document, '[os-element="card"][os-slot-step]');
    // WS2 cards contain qty step markers; WS3 slot cards use os-option-slot.
    // Select WS2's tier cards precisely: they carry os-option OR pb-option.
    var tierCards = [];
    each(qsa(document, '[os-element="card"]'), function (c) {
      if (tierOf(c) !== null && !c.hasAttribute('os-option-slot')) {
        tierCards.push(c);
      }
    });
    if (!tierCards.length) return false;

    function currentTier() {
      for (var i = 0; i < tierCards.length; i++) {
        if (tierCards[i].classList.contains('os--selected')) {
          var t = tierOf(tierCards[i]);
          if (t) return t;
        }
      }
      return 1;
    }

    function recompute() {
      var tier = currentTier();
      var per = PER_PAIR[tier] != null ? PER_PAIR[tier] : PER_PAIR[1];
      var saleTotal = per * tier;
      var regTotal = ANCHOR * tier;
      var pct = SAVINGS[tier] != null
        ? SAVINGS[tier]
        : Math.round((1 - saleTotal / regTotal) * 100);

      each(qsa(document, '[data-os-price="sale"]'), function (el) {
        el.textContent = money(saleTotal);
      });
      each(qsa(document, '[data-os-price="regular"]'), function (el) {
        el.textContent = money(regTotal);
      });
      each(qsa(document, '[data-os-price="saving-percent"]'), function (el) {
        el.textContent = pct + '% OFF';
      });
      // carry the chosen tier through to the cart page so it opens on that pair count
      each(qsa(document, 'a[href*="ws3" i]'), function (a) {
        var base = (a.getAttribute('href') || '/pages/ws3').split('?')[0];
        a.setAttribute('href', base + '?pairs=' + tier);
      });
    }

    each(tierCards, function (card) {
      if (card.__wsfnlBound) return;
      card.__wsfnlBound = true;
      card.addEventListener('click', function () {
        each(tierCards, function (c) { c.classList.remove('os--selected'); });
        card.classList.add('os--selected');
        recompute();
      });
    });

    recompute();
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 2. CART PAGE (WS3) — add-a-color slots + per-slot color/size selects
   *
   *   Slot cards:  [os-option-slot="N"] (N = 1..3). Slot 1 is always active;
   *                slots 2 & 3 start disabled (.is-disabled) until revealed.
   *   Empty slots: [os-component-empty-slot="N"] wrap the add-slot buttons
   *                (<a data-osaction="add-slot">). Empty-slot N is enabled once
   *                slot (N-1) is filled; clicking it reveals slot card N and
   *                enables the next empty slot. Filled empty slots gain a check.
   *   Selects:     select[data-oscomponent="color-select"] swaps the card's
   *                shown product image; size-select is stored (all in stock).
   *   Totals:      recomputed from the count of active pairs, priced at the
   *                matching tier (2 active → $63/ea, 3 → $56/ea).
   * ---------------------------------------------------------------------- */

  function initCart() {
    var slotCards = qsa(document, '[os-option-slot]');
    if (!slotCards.length) return false;

    // Index slot cards + empty slots by their number.
    var cardByN = {};
    each(slotCards, function (c) {
      var n = parseInt(c.getAttribute('os-option-slot'), 10);
      if (n) cardByN[n] = c;
    });
    var emptyByN = {};
    each(qsa(document, '[os-component-empty-slot]'), function (e) {
      var n = parseInt(e.getAttribute('os-component-empty-slot'), 10);
      if (n) emptyByN[n] = e;
    });

    var maxSlots = Object.keys(cardByN).length || 1;
    var HIDE = 'wsfnl-hide';
    var activePairs = 1;
    try { var qp = parseInt((location.search.match(/[?&]pairs=(\d+)/) || [])[1], 10); if (qp >= 1 && qp <= maxSlots) activePairs = qp; } catch (e) {}

    function recompute() {
      var pairs = activePairs;
      var per = PER_PAIR[pairs] != null ? PER_PAIR[pairs] : PER_PAIR[3];
      var saleTotal = per * pairs;
      var regTotal = ANCHOR * pairs;
      var pct = SAVINGS[pairs] != null
        ? SAVINGS[pairs]
        : Math.round((1 - saleTotal / regTotal) * 100);

      each(qsa(document, '[data-os-price="sale"]'), function (el) {
        el.textContent = money(saleTotal);
      });
      each(qsa(document, '[data-os-price="regular"]'), function (el) {
        el.textContent = money(regTotal);
      });
      each(qsa(document, '[data-os-price="saving-percent"]'), function (el) {
        el.textContent = pct + '% OFF';
      });
    }

    // Toggle the step check/number inside a container (empty slot or card).
    function markFilled(container, filled) {
      each(qsa(container, '[os-slot-step="icon"]'), function (el) {
        el.classList.toggle('is-active', filled);
      });
      each(qsa(container, '[os-slot-step="number"]'), function (el) {
        el.classList.toggle('is-active', !filled);
      });
    }

    // Enable / disable an empty-slot add button (visually + interaction).
    function setEmptyEnabled(n, enabled) {
      var e = emptyByN[n];
      if (!e) return;
      e.classList.toggle('is-disabled', !enabled);
    }

    // Render the whole ladder from the current activePairs count (self-consistent).
    function render() {
      for (var n = 1; n <= maxSlots; n++) {
        var card = cardByN[n], empty = emptyByN[n];
        var filled = n <= activePairs;
        if (card) {
          card.classList.remove('hide', 'hidden', 'os-hidden', 'w-condition-invisible');
          card.classList.toggle(HIDE, !filled);
          card.classList.toggle('os--active', filled);
          card.classList.toggle('is-disabled', !filled);
          card.style.display = filled ? '' : 'none';
          // a newly-revealed slot defaults to the first colour + first size (Black / US 6)
          if (filled && !card.__wsfnlDefaulted) { card.__wsfnlDefaulted = true; defaultSlot(card); }
        }
        if (empty) {
          var isNext = (n === activePairs + 1);   // only the very next add-a-color is enabled
          empty.classList.toggle(HIDE, filled);   // a consumed slot hides its add button
          empty.classList.toggle('is-disabled', !isNext);
          empty.style.display = filled ? 'none' : '';
          markFilled(empty, filled);
        }
      }
      recompute();
    }
    function addPair() { if (activePairs < maxSlots) { activePairs++; render(); } }
    function removePair() { if (activePairs > 1) { activePairs--; render(); } }

    // First non-placeholder option value ("Color"/"Size"/"Select…" are skipped).
    function firstRealValue(sel) {
      for (var i = 0; i < sel.options.length; i++) {
        var v = (sel.options[i].value || '').trim();
        var label = (sel.options[i].textContent || '').trim();
        if (v && !/^(color|size|select)\b/i.test(v) && !/^(select |color$|size$)/i.test(label)) return v;
      }
      return sel.options.length ? sel.options[0].value : '';
    }
    // Default a slot's colour + size to the first real option (Black / US 6) and swap its image.
    function defaultSlot(card) {
      var cSel = card.querySelector('select[data-oscomponent="color-select"]');
      var sSel = card.querySelector('select[data-oscomponent="size-select"]');
      if (cSel) { cSel.value = firstRealValue(cSel); cSel.dispatchEvent(new Event('change', { bubbles: true })); }
      if (sSel) { sSel.value = firstRealValue(sSel); sSel.dispatchEvent(new Event('change', { bubbles: true })); }
    }

    each(qsa(document, '[data-osaction="add-slot"]'), function (btn) {
      if (btn.__wsfnlBound) return;
      btn.__wsfnlBound = true;
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        var wrap = btn.closest('[os-component-empty-slot]');
        if (wrap && wrap.classList.contains('is-disabled')) return; // not yet unlocked
        addPair();
      });
    });

    each(qsa(document, '[data-osaction="remove-slot"]'), function (btn) {
      if (btn.__wsfnlBound) return;
      btn.__wsfnlBound = true;
      btn.addEventListener('click', function (ev) { ev.preventDefault(); removePair(); });
    });

    render(); // establish a clean, self-consistent initial state (1 pair by default)

    // Color <select> → swap this card's shown product image.
    each(qsa(document, 'select[data-oscomponent="color-select"]'), function (sel) {
      if (sel.__wsfnlBound) return;
      sel.__wsfnlBound = true;
      var card = sel.closest('[os-option-slot]') || document;
      var img = card.querySelector('.pb-quantity__media-2 img') ||
                card.querySelector('.pb-quantity__media-2 img, .os-card__wrapper img');
      function swap() {
        var opt = sel.options[sel.selectedIndex];
        var key = resolveColorKey(sel.value || (opt && opt.textContent));
        if (key && img && IMAGES[key]) {
          img.src = IMAGES[key];
          if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
        }
        // Reflect selection onto the hidden .color-card grid, if present.
        var grid = card.querySelector('.color_grid');
        if (grid) {
          each(qsa(grid, '.color-card'), function (c, i) {
            c.classList.toggle('os--active', i === sel.selectedIndex);
          });
        }
      }
      sel.addEventListener('change', swap);
      swap(); // sync the shown image to the default-selected colour on load
    });

    // Size <select> → record only (all sizes in stock; no availability gating).
    each(qsa(document, 'select[data-oscomponent="size-select"]'), function (sel) {
      if (sel.__wsfnlBound) return;
      sel.__wsfnlBound = true;
      sel.addEventListener('change', function () {
        sel.setAttribute('data-selected-size', sel.value);
      });
    });

    // Ladder initial state is already established by render() above.
    recompute();
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 3. RESERVATION CLOCK — [data-clock="tenmin"] counts DOWN mm:ss from 10:00,
   *    resets near zero so it is never stuck. Present on WS2 + WS3.
   * ---------------------------------------------------------------------- */

  function fmtClock(total) {
    var m = Math.floor(total / 60);
    var s = total % 60;
    return m + ':' + (s < 10 ? '0' + s : s);
  }

  function initClock() {
    var clocks = qsa(document, '[data-clock="tenmin"]');
    if (!clocks.length) return false;
    if (initClock.__timer) return true; // single shared ticker

    var remaining = RESERVE_SECONDS;
    function tick() {
      each(clocks, function (el) { el.textContent = fmtClock(remaining); });
      if (remaining <= 0) {
        remaining = RESERVE_SECONDS; // reset so it never sticks at 0
      } else {
        remaining -= 1;
      }
    }
    tick();
    initClock.__timer = window.setInterval(tick, 1000);
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 4. SCROLL PROGRESS BAR — [data-quiz="progress-pct"] width follows how far
   *    the reader has scrolled through the page (0% → 100%). On WS1.
   * ---------------------------------------------------------------------- */

  function initProgress() {
    var bars = qsa(document, '[data-quiz="progress-pct"]');
    if (!bars.length) return false;

    function update() {
      var doc = document.documentElement;
      var scrollable = (doc.scrollHeight - doc.clientHeight) || 1;
      var pct = Math.min(100, Math.max(0, (window.scrollY || doc.scrollTop || 0) / scrollable * 100));
      each(bars, function (el) { el.style.width = pct.toFixed(1) + '%'; });
    }

    if (!initProgress.__bound) {
      initProgress.__bound = true;
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }
    update();
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 5. SIZE GUIDE — "See Size Guide" opens the #wsfnl-sizeguide modal.
   * ---------------------------------------------------------------------- */

  function initSizeGuide() {
    var modal = document.getElementById('wsfnl-sizeguide');
    if (!modal) return false;
    function open(e) { if (e) e.preventDefault(); modal.classList.add('wsfnl-sizeguide-open'); }
    function close() { modal.classList.remove('wsfnl-sizeguide-open'); }
    each(qsa(document, '.text-block-946, [class*="size_guide"], .os-card__variant-label-link-2'), function (t) {
      if (t.__wsfnlSg) return; t.__wsfnlSg = true; t.style.cursor = 'pointer'; t.addEventListener('click', open);
    });
    each(qsa(modal, '.wsfnl-sg-close, .wsfnl-sg-backdrop'), function (c) {
      if (c.__wsfnlSg) return; c.__wsfnlSg = true; c.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 6. SHIP DATE — [data-shipsby]/.ships-by always shows a live upcoming date.
   * ---------------------------------------------------------------------- */

  function initShipsBy() {
    var els = qsa(document, '[data-shipsby], .ships-by');
    if (!els.length) return false;
    var d = new Date();
    d.setDate(d.getDate() + 2); // ships in ~2 days
    var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var n = d.getDate();
    var suf = (n % 10 === 1 && n !== 11) ? 'st' : (n % 10 === 2 && n !== 12) ? 'nd' : (n % 10 === 3 && n !== 13) ? 'rd' : 'th';
    var txt = DOW[d.getDay()] + ', ' + MON[d.getMonth()] + ' ' + n + suf;
    each(els, function (el) { el.textContent = txt; });
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 7. SALE COUNTDOWN — drives #js-clock-days/-hours/-minutes/-seconds from a
   *    single deterministic target (next local midnight) so every page shows
   *    the SAME time. Replaces the removed per-page inline getNextMidnight.
   * ---------------------------------------------------------------------- */

  function initSaleCountdown() {
    var days = document.getElementById('js-clock-days');
    var hrs = document.getElementById('js-clock-hours');
    var min = document.getElementById('js-clock-minutes');
    var sec = document.getElementById('js-clock-seconds');
    if (!hrs && !min && !sec) return false;
    if (initSaleCountdown.__timer) return true;
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function tick() {
      var end = new Date(); end.setHours(24, 0, 0, 0); // next local midnight — identical on all pages
      var total = Math.max(0, Math.floor((end.getTime() - Date.now()) / 1000));
      var d = Math.floor(total / 86400); total -= d * 86400;
      var h = Math.floor(total / 3600); total -= h * 3600;
      var m = Math.floor(total / 60); var s = total - m * 60;
      if (days) days.textContent = pad(d);
      if (hrs) hrs.textContent = pad(h);
      if (min) min.textContent = pad(m);
      if (sec) sec.textContent = pad(s);
    }
    tick();
    // Self-aligning tick: schedule each update just after the wall-clock second
    // boundary so the seconds never skip or repeat (naive setInterval drifts and
    // the recompute-from-clock then shows a jumped/duplicated second).
    (function loop() {
      var delay = 1000 - (Date.now() % 1000);
      initSaleCountdown.__timer = window.setTimeout(function () { tick(); loop(); }, delay);
    })();
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 8. BENEFIT TABS — self-sufficient Webflow-style tab switching (WS2).
   *    Clicking a [data-w-tab] link activates the matching pane. Idempotent.
   * ---------------------------------------------------------------------- */

  function initTabs() {
    var links = qsa(document, '.w-tab-link[data-w-tab], [data-w-tab].tab_tab-link');
    if (!links.length) return false;
    each(links, function (link) {
      if (link.__wsfnlTab) return; link.__wsfnlTab = true;
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var name = link.getAttribute('data-w-tab');
        var menu = link.closest('.w-tab-menu') || link.parentElement;
        var tabs = link.closest('.w-tabs') || document;
        each(qsa(menu, '.w-tab-link, [data-w-tab]'), function (l) {
          if (!l.getAttribute('data-w-tab')) return;
          var on = l === link;
          l.classList.toggle('w--current', on);
          l.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        each(qsa(tabs, '.w-tab-pane'), function (pane) {
          var on = pane.getAttribute('data-w-tab') === name;
          pane.classList.toggle('w--tab-active', on);
          pane.style.display = on ? 'block' : 'none';
          pane.style.opacity = on ? '1' : '';
        });
      });
    });
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 9. CHECKOUT CTA — os-checkout="submit" advances to the checkout step.
   * ---------------------------------------------------------------------- */

  // Collect the active bundle slots' chosen colour + size (cart page WS3).
  function collectPairs() {
    var pairs = [];
    each(qsa(document, '[os-option-slot]'), function (card) {
      var cs = getComputedStyle(card);
      if (card.classList.contains('wsfnl-hide') || cs.display === 'none') return; // inactive slot
      var cSel = card.querySelector('select[data-oscomponent="color-select"]');
      var sSel = card.querySelector('select[data-oscomponent="size-select"]');
      var color = cSel ? String(cSel.value || '').trim().toLowerCase() : '';
      var size = sSel ? String(sSel.value || '').trim() : '';
      if (color) pairs.push({ color: color, size: size });
    });
    return pairs;
  }

  // Resolve a (colour, size) to the copy product's numeric variant id.
  function variantId(color, size) {
    var map = window.WSFNL_VARIANTS || {};
    color = String(color || '').trim().toLowerCase();
    size = String(size || '').trim();
    if (map[color + '|' + size]) return map[color + '|' + size];
    var n = (size.match(/\d+(?:\.\d+)?/) || [])[0];      // "US Women 6" / "6" -> "US 6"
    if (n && map[color + '|US ' + n]) return map[color + '|US ' + n];
    // fall back to the first in-stock size for that colour so checkout never dead-ends
    for (var k in map) { if (map.hasOwnProperty(k) && k.indexOf(color + '|') === 0) return map[k]; }
    return null;
  }

  // Read the Taboola/ecomworld click id (URL param or cf_click_id cookie). ecomworld
  // writes this to the cart asynchronously, but our immediate checkout redirect can
  // abort that write — so we stamp it ourselves before leaving, or the purchase never
  // attributes back to the ad click. Returns '' when absent (then behaviour is unchanged).
  function wsfnlClickId() {
    try {
      var p = new URLSearchParams(window.location.search);
      var u = p.get('cf_click_id') || p.get('click_id');
      if (u) return u;
      var parts = ('; ' + document.cookie).split('; cf_click_id=');
      if (parts.length >= 2) { var v = parts.pop().split(';').shift(); if (v) return v; }
    } catch (e) { /* isolate */ }
    return '';
  }

  function initCheckout() {
    var btns = qsa(document, '[os-checkout="submit"], [os-checkout-button="submit"]');
    if (!btns.length) return false;
    each(btns, function (b) {
      if (b.__wsfnlCo) return; b.__wsfnlCo = true;
      b.addEventListener('click', function (e) {
        e.preventDefault();
        if (b.__busy) return; b.__busy = true;
        b.classList.add('wsfnl-checkout-loading'); // reveal the spinner so it's clear it's working
        var items = [];
        each(collectPairs(), function (p) { var v = variantId(p.color, p.size); if (v) items.push({ id: v, quantity: 1 }); });
        if (!items.length) { window.location.href = '/cart'; return; }
        try { fbTrack('InitiateCheckout', pixelParams({ value: tierValue(items.length), num_items: items.length })); } catch (e2) {}
        var codes = window.WSFNL_BUNDLE_CODES || {};
        var code = codes[items.length] || (items.length >= 3 ? codes[3] : items.length >= 2 ? codes[2] : '');
        // clear cart -> add the selected pairs -> apply bundle code -> checkout
        fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
          .then(function () { return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items }) }); })
          .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
          .then(function () {
            var dest = code ? ('/discount/' + code + '?redirect=/checkout') : '/checkout';
            var cfid = wsfnlClickId();
            if (!cfid) { window.location.href = dest; return; } // no click id -> unchanged behaviour
            var done = false, go = function () { if (done) return; done = true; window.location.href = dest; };
            setTimeout(go, 1200); // hard cap: tracking must never block checkout
            fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { cf_click_id: cfid } }) }).then(go, go);
          })
          .catch(function () { b.__busy = false; b.classList.remove('wsfnl-checkout-loading'); window.location.href = '/cart'; });
      });
    });
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 10. STICKY-NAV REVEAL — the removed IX2 runtime used to fade in the
   *     mobile sticky bar on scroll. Reproduce it (opacity toggle past 600px).
   * ---------------------------------------------------------------------- */

  function initStickyReveal() {
    var bars = qsa(document, '.sticky-nav, #sticky_bar, .sticky_bar');
    if (!bars.length) return false;
    function update() {
      var show = (window.scrollY || document.documentElement.scrollTop || 0) > 600;
      each(bars, function (b) {
        b.style.opacity = show ? '1' : '0';
        b.style.pointerEvents = show ? 'auto' : 'none';
      });
    }
    if (!initStickyReveal.__b) {
      initStickyReveal.__b = true;
      window.addEventListener('scroll', update, { passive: true });
    }
    update();
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 11. FAQ ACCORDION — self-sufficient (Finsweet runtime was removed).
   *     [fs-accordion-element="trigger"] toggles its sibling content; single-open.
   * ---------------------------------------------------------------------- */

  function initAccordion() {
    var triggers = qsa(document, '[fs-accordion-element="trigger"]');
    if (!triggers.length) return false;
    each(triggers, function (t) {
      if (t.__wsfnlAcc) return; t.__wsfnlAcc = true;
      var item = t.closest('[fs-accordion-element="accordion"]') || t.parentElement;
      var content = item ? item.querySelector('[fs-accordion-element="content"]') : null;
      // the captured CSS collapses content with max-height:0 !important — beat it with inline !important
      function setMax(el, v) { if (el) el.style.setProperty('max-height', v, 'important'); }
      if (content) {
        content.style.setProperty('display', 'block', 'important');
        content.style.setProperty('overflow', 'hidden', 'important');
        content.style.transition = 'max-height .3s ease';
        setMax(content, '0px');
      }
      t.setAttribute('aria-expanded', 'false');
      t.style.cursor = 'pointer';
      t.addEventListener('click', function () {
        var open = t.getAttribute('aria-expanded') === 'true';
        var group = t.closest('[fs-accordion-element="group"]');
        if (group && !open) { // single-open: collapse siblings first
          each(qsa(group, '[fs-accordion-element="trigger"]'), function (o) {
            o.setAttribute('aria-expanded', 'false');
            var oi = o.closest('[fs-accordion-element="accordion"]');
            setMax(oi && oi.querySelector('[fs-accordion-element="content"]'), '0px');
            var oa = oi && oi.querySelector('[fs-accordion-element="arrow"]');
            if (oa) oa.style.transform = '';
          });
        }
        t.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (content) {
          var body = content.firstElementChild;
          var h = Math.max(content.scrollHeight, body ? body.scrollHeight + 24 : 0, 0);
          setMax(content, open ? '0px' : (h > 20 ? h + 20 : 600) + 'px');
        }
        var arrow = item && item.querySelector('[fs-accordion-element="arrow"]');
        if (arrow) arrow.style.transform = open ? '' : 'rotate(180deg)';
      });
    });
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 11b. PRODUCT GALLERY (WS2) — populate the swiper slides + thumbnails with
   *      the full PDP image set (hero + marketing cards + every colour).
   * ---------------------------------------------------------------------- */

  // Generic single-image gallery controller. Swiper was removed, so we show one
  // slide and swap its src on thumbnail/arrow interaction. Works for any variant
  // via opts: compSel, thumbSel, thumbWrapSel, images.
  function wireGallery(opts) {
    var comp = document.querySelector(opts.compSel);
    if (!comp || comp.__wsfnlGallery) return false;
    var images = opts.images;
    if (!images || !images.length) return false;
    var wrapSel = opts.thumbWrapSel || '.swiper-slide';

    var wrap = comp.querySelector('.swiper-wrapper');
    var slides = wrap ? qsa(wrap, '.swiper-slide') : [];
    try { if (comp.swiper && comp.swiper.destroy) comp.swiper.destroy(true, true); } catch (e) {}
    if (wrap) { wrap.style.setProperty('transform', 'none', 'important'); wrap.style.transition = 'none'; }
    each(slides, function (s, i) { s.style.display = i === 0 ? 'block' : 'none'; });
    var mainImg = slides[0] ? slides[0].querySelector('img') : null;
    if (!mainImg) return false;

    var thumbs = opts.thumbSel ? qsa(document, opts.thumbSel) : [];
    // Ensure one thumbnail per image (clone the last if the source has too few).
    if (thumbs.length && thumbs.length < images.length) {
      var tmpl = thumbs[thumbs.length - 1].closest(wrapSel) || thumbs[thumbs.length - 1].parentElement;
      for (var k = thumbs.length; k < images.length; k++) { var cl = tmpl.cloneNode(true); cl.style.display = ''; tmpl.parentElement.appendChild(cl); }
      thumbs = qsa(document, opts.thumbSel);
    }

    function fitMain(img) {
      img.removeAttribute('srcset'); img.removeAttribute('data-src');
      img.style.setProperty('object-fit', 'contain', 'important');
      img.style.setProperty('width', '100%', 'important');
      img.style.setProperty('height', '100%', 'important');
      img.style.background = '#ffffff';
    }
    each(thumbs, function (img, i) {
      var w = img.closest(wrapSel) || img.parentElement;
      if (i >= images.length) { if (w) w.style.display = 'none'; return; }
      img.setAttribute('src', images[i]); img.removeAttribute('srcset'); img.removeAttribute('data-src');
      img.style.setProperty('object-fit', 'cover', 'important');
    });

    function show(i) {
      var n = ((i % images.length) + images.length) % images.length;
      comp.__wsfnlActive = n;
      mainImg.setAttribute('src', images[n]); fitMain(mainImg);
      each(thumbs, function (t, ti) {
        var w = t.closest(wrapSel) || t;
        if (ti === n) w.classList.add('wsfnl-thumb-active'); else w.classList.remove('wsfnl-thumb-active');
      });
    }

    // Thumbnails — capture-phase + stopPropagation beats any stale handler.
    each(thumbs, function (img, i) {
      if (i >= images.length) return;
      var w = img.closest(wrapSel) || img.parentElement;
      if (w.__wsfnlThumb) return; w.__wsfnlThumb = true; w.style.cursor = 'pointer';
      w.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); show(i); }, true);
    });
    // Arrows — Swiper (CDN) re-inits AFTER us and hijacks/locks the buttons (it
    // swallows the prev click entirely). Our first destroy ran before Swiper had
    // loaded, so: destroy Swiper again once it exists (tame), keep the wrapper
    // pinned, force the arrows visible, and delegate the click on the component
    // (capture + stopImmediatePropagation) so it works regardless.
    // Swiper (CDN) hijacks the arrow buttons — it even swallows the prev click
    // entirely. Kill Swiper once it exists, then CLONE each arrow (cloneNode drops
    // every Swiper/Webflow listener) and bind our own click on the clean element.
    var ARROWS = [['.swiper__button--prev', -1], ['.swiper__button--next', 1]];
    function tame() {
      try { if (comp.swiper && comp.swiper.destroy) comp.swiper.destroy(true, true); } catch (e) {}
      if (wrap) wrap.style.setProperty('transform', 'none', 'important');
      each(ARROWS, function (pair) {
        var b = comp.querySelector(pair[0]);
        if (!b) return;
        if (!b.__wsfnlFresh) {
          var c = b.cloneNode(true);
          c.__wsfnlFresh = true;
          if (b.parentNode) b.parentNode.replaceChild(c, b);
          b = c;
          var dir = pair[1];
          b.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); show((comp.__wsfnlActive || 0) + dir); });
        }
        b.classList.remove('swiper-button-disabled', 'swiper-button-lock');
        b.style.setProperty('display', 'flex', 'important');
        b.style.setProperty('pointer-events', 'auto', 'important');
        b.style.setProperty('opacity', '1', 'important');
        b.style.cursor = 'pointer'; b.removeAttribute('aria-disabled');
      });
    }
    tame();
    if (!comp.__wsfnlArrows) {
      comp.__wsfnlArrows = true;
      window.setTimeout(tame, 600); window.setTimeout(tame, 1600); window.setTimeout(tame, 2600);
    }

    comp.__wsfnlGallery = true;
    show(0);
    return true;
  }

  function initGallery() {
    if (!GALLERY.length) return false;
    // WS3 wants one clean photo per colour: Black/White/Grey/Beige/Pink
    // (GALLERY[1..3] are marketing infographics — excluded).
    var COLORS5 = [GALLERY[0], GALLERY[4], GALLERY[5], GALLERY[6], GALLERY[7]].filter(Boolean);
    var did = false;
    if (document.querySelector('.slider_component.is-v6')) {
      did = wireGallery({ compSel: '.slider_component.is-v6', thumbSel: '.product-thumbnails-holder img', thumbWrapSel: '.swiper-slide', images: GALLERY }) || did;
    }
    if (document.querySelector('.slider_component.is-sw20')) {
      did = wireGallery({ compSel: '.slider_component.is-sw20', thumbSel: '.swiper20_thumb img', thumbWrapSel: '.swiper20_thumb', images: COLORS5 }) || did;
    }
    if (!did && document.querySelector('.slider_component')) {
      wireGallery({ compSel: '.slider_component', thumbSel: '.product-thumbnails-holder img', thumbWrapSel: '.swiper-slide', images: GALLERY });
    }
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 11c. BRAND COLOURS — force any residual olive-green CTA to navy. A green
   *      rule lives inside a desktop @media block that CSS can't cleanly beat;
   *      an inline !important wins over media-query rules.
   * ---------------------------------------------------------------------- */

  function initBrandColors() {
    var navy = '#22335A';
    each(qsa(document, '.button-sm-2, .button-sm-2.sticky-btn'), function (b) {
      b.style.setProperty('transition', 'none', 'important'); // snap — no green→navy flash
      b.style.setProperty('background-color', navy, 'important');
      b.style.setProperty('border-color', navy, 'important');
    });
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 11d. VIDEO AUTOPLAY — muted listicle/card videos; browsers pause offscreen
   *      muted autoplay, so (re)play them when they scroll into view.
   * ---------------------------------------------------------------------- */

  function initVideos() {
    var vids = qsa(document, '.image-wrapper-6 video, .wsfnl-card-media, .wsfnl-listicle-media');
    if (!vids.length) return false;
    function play(v) { try { v.muted = true; var p = v.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {} }
    each(vids, function (v) { if (v.tagName === 'VIDEO') play(v); });
    if ('IntersectionObserver' in window && !initVideos.__io) {
      initVideos.__io = new IntersectionObserver(function (entries) {
        each(entries, function (e) { if (e.isIntersecting && e.target.tagName === 'VIDEO') play(e.target); });
      }, { threshold: 0.15 });
      each(vids, function (v) { if (v.tagName === 'VIDEO') initVideos.__io.observe(v); });
    }
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 11e. STICKY STACK (WS1) — the publisher masthead ("Healthy Foot Insider")
   *      was static, so it scrolled away and left a gap between the sticky sale
   *      bar and the sticky progress bar. Make it sticky and stack all three by
   *      measured height so there is no gap on any viewport.
   * ---------------------------------------------------------------------- */

  function initStickyStack() {
    var sale = document.querySelector('.sticky-headers-4') || document.querySelector('[class*="sticky-headers"]');
    var hfi = document.querySelector('.nav-foots.publisher');
    var prog = document.querySelector('.quiz_progress_wrapper-4');
    if (!hfi && !prog) return false;
    function apply() {
      var sh = sale ? Math.round(sale.getBoundingClientRect().height) : 0;
      if (hfi) {
        hfi.style.setProperty('position', 'sticky', 'important');
        hfi.style.setProperty('top', sh + 'px', 'important');
        hfi.style.setProperty('z-index', '80', 'important');
      }
      var hh = hfi ? Math.round(hfi.getBoundingClientRect().height) : 0;
      if (prog) prog.style.setProperty('top', (sh + hh) + 'px', 'important');
    }
    apply();
    if (!initStickyStack.__bound) {
      initStickyStack.__bound = true;
      window.addEventListener('resize', apply, { passive: true });
      window.addEventListener('load', apply);
      window.setTimeout(apply, 500);
    }
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 11f. META PIXEL EVENTS — {% layout none %} funnel pages fire only the base
   *      PageView. Fire the standard funnel events (ViewContent, AddToCart,
   *      InitiateCheckout) through the store's callable fbq so tracking matches
   *      a normal PDP. Purchase fires on Shopify's own thank-you-page pixel.
   * ---------------------------------------------------------------------- */

  var PIXEL = {
    ids: (DATA.pixelContentIds && DATA.pixelContentIds.length) ? DATA.pixelContentIds : ['8220790522025'],
    name: DATA.pixelName || 'WideStep BareFlex Pro',
    currency: DATA.pixelCurrency || 'USD'
  };
  function tierValue(t) { t = t || 1; return (PER_PAIR[t] != null ? PER_PAIR[t] : PER_PAIR[1]) * t; }
  function pixelParams(extra) {
    var p = { content_ids: PIXEL.ids, content_type: 'product', content_name: PIXEL.name, currency: PIXEL.currency };
    if (extra) for (var k in extra) p[k] = extra[k];
    return p;
  }
  function fbTrack(ev, params) {
    function fire() {
      if (typeof window.fbq !== 'function') return false;
      try { window.fbq('track', ev, params, { eventID: 'wsfnl-' + ev + '-' + (fbTrack.__n = (fbTrack.__n || 0) + 1) }); } catch (e) {}
      return true;
    }
    if (fire()) return;                 // fbq loads async via content_for_header —
    var n = 0, iv = window.setInterval(function () { if (fire() || ++n > 40) window.clearInterval(iv); }, 250); // retry up to ~10s
  }
  function selectedTier() {
    var u = (location.search || '').match(/pairs=(\d)/);      // WS3: tier carried in the URL
    if (u) return parseInt(u[1], 10);
    var link = document.querySelector('a[href*="ws3" i]');    // WS2: tier on the offer CTA link
    var m = link && (link.getAttribute('href') || '').match(/pairs=(\d)/);
    if (m) return parseInt(m[1], 10);
    var sel = document.querySelector('[os-element="card"].os--selected');
    return sel ? (tierOf(sel) || 1) : 1;
  }

  // ecomworld/Taboola view_content — ecomworld can't auto-detect these {% layout none %}
  // funnel pages as products, so it never fires view_content here. Fire it ourselves on
  // the advertorial/offer pages (WS1/WS2), once, and stamp the click id into the cart
  // early. Same page detection as initPixel. Fully isolated; no-op without a click id.
  function initEcomView() {
    if (initEcomView.__done) return;
    var isCart = /ws3/i.test(location.pathname) || !!document.querySelector('[os-option-slot]');
    if (isCart) return;                       // WS3 fires add_to_cart at checkout instead
    var cfid = wsfnlClickId();
    if (!cfid) return;                         // no ad click -> nothing to attribute
    initEcomView.__done = true;
    try { new Image().src = 'https://trk.ecomworld.blog/cf/cv?cf_click_id=' + encodeURIComponent(cfid) + '&ct=view_content&t=' + (+new Date()); } catch (e) { /* isolate */ }
    try { fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { cf_click_id: cfid } }) }); } catch (e) { /* isolate */ }
  }

  function initPixel() {
    var isCart = /ws3/i.test(location.pathname) || !!document.querySelector('[os-option-slot]');
    if (!isCart) {
      // ViewContent — advertorial + offer pages (WS1/WS2), once.
      if (!initPixel.__vc) {
        initPixel.__vc = true;
        fbTrack('ViewContent', pixelParams({ value: (PER_PAIR[1] != null ? PER_PAIR[1] : 69) }));
      }
    } else if (!initPixel.__atc) {
      // AddToCart — reaching the bundle builder (WS3) means the offer was added
      // to cart. Page-load trigger is reliable; the OS/PageFly CTA elements
      // swallow click events so per-element/delegated hooks can't catch them.
      initPixel.__atc = true;
      var t = selectedTier();
      fbTrack('AddToCart', pixelParams({ value: tierValue(t), num_items: t }));
    }
    return true;
  }

  /* ---------------------------------------------------------------------- *
   * 12. Boot — idempotent. Each initializer no-ops when its DOM is absent.
   * ---------------------------------------------------------------------- */

  function boot() {
    if (boot.__ran) return;
    boot.__ran = true;
    try { initOffer(); } catch (e) { /* isolate */ }
    try { initCart(); } catch (e) { /* isolate */ }
    try { initClock(); } catch (e) { /* isolate */ }
    try { initProgress(); } catch (e) { /* isolate */ }
    try { initSizeGuide(); } catch (e) { /* isolate */ }
    try { initShipsBy(); } catch (e) { /* isolate */ }
    try { initSaleCountdown(); } catch (e) { /* isolate */ }
    try { initTabs(); } catch (e) { /* isolate */ }
    try { initCheckout(); } catch (e) { /* isolate */ }
    try { initStickyReveal(); } catch (e) { /* isolate */ }
    try { initAccordion(); } catch (e) { /* isolate */ }
    try { initBrandColors(); } catch (e) { /* isolate */ }
    try { initGallery(); } catch (e) { /* isolate */ }
    try { initStickyStack(); } catch (e) { /* isolate */ }
    try { initPixel(); } catch (e) { /* isolate */ }
    try { initEcomView(); } catch (e) { /* isolate */ }
    // Belt-and-suspenders: physically remove the gallery fire-tag booster + dead
    // video-play overlay so they can never render (mobile blew the fire up huge).
    try { each(qsa(document, '.slider_component .pb-booster, .slider_component .video_wrapper, .slider_component .code-embed-15'), function (e) { e.parentNode && e.parentNode.removeChild(e); }); } catch (e) { /* isolate */ }
    try { initVideos(); } catch (e) { /* isolate */ }
    // swiper lazy-loads slide images; re-apply the gallery after it settles
    window.setTimeout(function () { try { initGallery(); } catch (e) {} }, 700);
    window.setTimeout(function () { try { initGallery(); } catch (e) {} }, 1800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
